import React, { useState, useEffect } from 'react';
import { Megaphone } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';

const ChangelogWidget = ({ onClick, variant = 'button', className = '' }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUnreadCount();

    // Subscribe to changelog changes
    const channel = supabase
      .channel('changelog-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'app_changelog'
        },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get all published changelog entries
      const { data: publishedEntries, error: entriesError } = await supabase
        .from('app_changelog')
        .select('id')
        .eq('is_published', true)
        .order('published_date', { ascending: false });

      if (entriesError) throw entriesError;

      if (!publishedEntries || publishedEntries.length === 0) {
        setUnreadCount(0);
        setLoading(false);
        return;
      }

      // Get user's viewed entries
      const { data: viewedEntries, error: viewsError } = await supabase
        .from('changelog_views')
        .select('changelog_id')
        .eq('user_id', user.id);

      if (viewsError) throw viewsError;

      // Calculate unread count
      const viewedIds = new Set((viewedEntries || []).map(v => v.changelog_id));
      const unread = publishedEntries.filter(entry => !viewedIds.has(entry.id));
      
      setUnreadCount(unread.length);
    } catch (error) {
      console.error('Error fetching unread changelog count:', error);
    } finally {
      setLoading(false);
    }
  };

  // Header variant: a neutral icon button like Cart/Notifications, so the header's
  // only yellow control is the primary action (Search).
  if (variant === 'icon') {
    return (
      <button
        onClick={onClick}
        className={`relative text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors duration-200 ${className || 'p-2'}`}
        title="What's New"
        aria-label={unreadCount > 0 ? `What's New, ${unreadCount} unread` : "What's New"}
      >
        <Megaphone className="h-6 w-6" aria-hidden="true" />
        {!loading && unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold leading-none flex items-center justify-center ring-2 ring-white tabular-nums bg-red-600 text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className="relative inline-flex items-center px-3 py-2 bg-brand hover:bg-brand-hover text-black font-medium rounded-lg shadow-sm transition-colors duration-200"
      title="View changelog and updates"
    >
      <Megaphone className="h-5 w-5 mr-2" />
      <span className="hidden sm:inline">What's New</span>
      
      <AnimatePresence>
        {!loading && unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full border-2 border-white"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

export default ChangelogWidget;
